import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { ensureNamespaces } from "../../i18n/i18";
import {
    FaGlobe, FaPrint, FaLink,
    FaInfoCircle, FaCheckCircle, FaChartBar, FaFileAlt, FaMoneyBill, FaCalendarAlt
} from "react-icons/fa";

function Section({ id, title, children }) {
    return (
        <section id={id} className="scroll-mt-24">
            <header className="flex items-center gap-2 mb-3">
                <a href={`#${id}`} className="text-slate-400 hover:text-slate-600" aria-label="Copiar enlace">
                    <FaLink />
                </a>
                <h2 className="text-xl font-semibold">{title}</h2>
            </header>
            <div className="prose prose-slate max-w-none">{children}</div>
            <div className="h-px bg-slate-200 my-8" />
        </section>
    );
}

function ImageBlock({ alt }) {
    return (
        <div className="border border-dashed border-slate-300 rounded-lg p-4 text-center text-slate-500">
            {/* Sustituye este DIV por tu imagen real: */}
            {/* <img src="/ruta/a/tu/captura.png" alt={alt} className="rounded-lg shadow" /> */}
            {alt} — (Aqui va imagen)
        </div>
    );
}

export default function Manual() {
    const [lang, setLang] = useState(i18n.language || "es");

    // t = manual, tc = common
    const { t } = useTranslation("manual");
    const { t: tc } = useTranslation("common");

    useEffect(() => {
        // precarga ambos namespaces
        ensureNamespaces(lang, ["common", "manual"]);
    }, [lang]);

    const switchLang = async () => {
        const next = lang === "es" ? "en" : "es";
        await ensureNamespaces(next, ["common", "manual"]);
        await i18n.changeLanguage(next);
        try { localStorage.setItem("lang", next); } catch { }
        setLang(next);
    };

    // Índice/TOC
    const TOC = [
        { id: "access", icon: <FaCheckCircle className="opacity-70" />, label: t("access.title", { defaultValue: "Acceso y sesión" }) },
        { id: "dashboard", icon: <FaChartBar className="opacity-70" />, label: t("dashboard.title", { defaultValue: "Dashboard" }) },
        { id: "reports", icon: <FaFileAlt className="opacity-70" />, label: t("reports.title", { defaultValue: "Reportes" }) },
        { id: "finance", icon: <FaMoneyBill className="opacity-70" />, label: t("finance.title", { defaultValue: "Finanzas" }) },
        { id: "calendar", icon: <FaCalendarAlt className="opacity-70" />, label: t("calendar.title", { defaultValue: "Calendario" }) },
        { id: "tips", icon: <FaInfoCircle className="opacity-70" />, label: t("tips.title", { defaultValue: "Tips" }) },
        { id: "support", icon: <FaInfoCircle className="opacity-70" />, label: t("support.title", { defaultValue: "Soporte" }) },
    ];

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Hero */}
            <header className="bg-slate-900 text-white px-6 py-6">
                <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-bold">{t("title")}</h1>

                    <span className="text-white/70 ml-auto">
                        {tc("lastUpdate")}: {new Date().toLocaleDateString(lang === "es" ? "es-MX" : "en-US")}
                    </span>

                    <button onClick={() => window.print()} className="ml-4 px-3 py-2 rounded bg-cyan-600 hover:bg-cyan-700">
                        <FaPrint className="inline mr-2" /> {tc("print")}
                    </button>

                    <button onClick={switchLang} className="ml-2 px-3 py-2 rounded bg-slate-700 hover:bg-slate-600">
                        <FaGlobe className="inline mr-2" /> {lang === "es" ? "EN" : "ES"}
                    </button>
                </div>
            </header>

            {/* Body */}
            <div className="max-w-6xl mx-auto px-6 py-8 grid md:grid-cols-[250px_1fr] gap-8">
                {/* Índice sticky */}
                <aside className="md:sticky md:top-6 h-max bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">
                        {t("toc", { defaultValue: "Contenido" })}
                    </h3>
                    <nav className="space-y-2 text-sm">
                        {TOC.map((item) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                className="flex items-center gap-2 text-slate-600 hover:text-cyan-700"
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </a>
                        ))}
                    </nav>
                </aside>

                {/* Contenido */}
                <main className="space-y-8">


                    {/* Acceso y sesión */}
                    <Section id="access" title={t("access.title", { defaultValue: "Acceso y sesión" })}>
                        <ul className="list-disc pl-5 space-y-1">
                            {(t("access.items", { returnObjects: true, defaultValue: [] }) || []).map((li, i) => (
                                <li key={i}>{li}</li>
                            ))}
                        </ul>
                    </Section>

                    {/* Dashboard */}
                    <Section id="dashboard" title={t("dashboard.title", { defaultValue: "Dashboard" })}>
                        <p className="mb-4">{t("dashboard.p", { defaultValue: "" })}</p>
                        <ImageBlock alt={t("dashboard.kpisAlt", { defaultValue: "KPIs y tarjetas" })} />
                        <ul className="list-disc pl-5 mt-4 space-y-1">
                            {(t("dashboard.actions", { returnObjects: true, defaultValue: [] }) || []).map((li, i) => (
                                <li key={i}>{li}</li>
                            ))}
                        </ul>
                    </Section>

                    {/* Reportes */}
                    <Section id="reports" title={t("reports.title", { defaultValue: "Reportes" })}>
                        <p>{t("reports.filters", { defaultValue: "" })}</p>
                        <div className="my-4 grid gap-4">
                            <ImageBlock alt={t("reports.filtersAlt", { defaultValue: "Filtros de reportes" })} />
                            <p className="text-sm text-slate-600">{t("reports.buttons", { defaultValue: "" })}</p>
                        </div>
                        <ul className="list-disc pl-5 space-y-1">
                            {(t("reports.tips", { returnObjects: true, defaultValue: [] }) || []).map((li, i) => (
                                <li key={i}>{li}</li>
                            ))}
                        </ul>
                    </Section>

                    {/* Finanzas */}
                    <Section id="finance" title={t("finance.title", { defaultValue: "Finanzas" })}>
                        <p>{t("finance.p", { defaultValue: "" })}</p>
                        <p className="text-slate-600 text-sm">{t("finance.colors", { defaultValue: "" })}</p>
                        <div className="my-4 grid gap-4">
                            <ImageBlock alt={t("finance.movementsAlt", { defaultValue: "Movimientos y saldos" })} />
                        </div>
                        <ul className="list-disc pl-5 space-y-1">
                            {(t("finance.actions", { returnObjects: true, defaultValue: [] }) || []).map((li, i) => (
                                <li key={i}>{li}</li>
                            ))}
                        </ul>
                    </Section>

                    {/* Calendario */}
                    <Section id="calendar" title={t("calendar.title", { defaultValue: "Calendario" })}>
                        <p className="mb-4">{t("calendar.p", { defaultValue: "" })}</p>
                        <ImageBlock alt={t("calendar.alt", { defaultValue: "Calendario de CDRs" })} />
                    </Section>

                    {/* Tips */}
                    <Section id="tips" title={t("tips.title", { defaultValue: "Tips" })}>
                        <ul className="list-disc pl-5 space-y-1">
                            {(t("tips.list", { returnObjects: true, defaultValue: [] }) || []).map((li, i) => (
                                <li key={i}>{li}</li>
                            ))}
                        </ul>
                    </Section>

                    {/* Soporte */}
                    <Section id="support" title={t("support.title", { defaultValue: "Soporte" })}>
                        <p>{t("support.p", { defaultValue: "" })}</p>
                        <ul className="list-disc pl-5 space-y-1">
                            {(t("support.contact", { returnObjects: true, defaultValue: [] }) || []).map((li, i) => (
                                <li key={i}>{li}</li>
                            ))}
                        </ul>
                    </Section>
                </main>
            </div>

            {/* Print styles básicos */}
            <style>{`
        @media print {
          nav, aside button, .bg-slate-900 { display: none !important; }
          .shadow-sm, .border, .rounded-xl { box-shadow: none !important; }
          a[href^="#"]::after { content: ""; }
          main { break-inside: avoid; }
        }
      `}</style>
        </div>
    );
}
